import type { Definition } from '@survey/core';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { SurveyDetail } from '../api/types';
import { FlowEditor } from '../components/builder/FlowEditor';
import { Topbar } from '../components/Layout';
import { PageLoader } from '../components/ui';
import { useAutosave } from '../hooks/useAutosave';
import { useI18n } from '../i18n';
import { SaveIndicator } from './Builder';

/**
 * The survey flow on its own page, so it can have the whole window or live in a
 * second tab next to the builder. It saves the definition the same way the
 * builder does (autosave with the revision check): if the other tab saved in
 * between, this one stops and asks to reload instead of overwriting it.
 */
export function FlowPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const [survey, setSurvey] = useState<SurveyDetail | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setSurvey(await api.getSurvey(id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('builder.load_failed'));
    }
  }, [id, t]);
  useEffect(() => {
    setSurvey(null);
    void load();
  }, [load]);

  if (error) return <div className="banner error">{error}</div>;
  if (!survey) return <PageLoader />;
  if (!survey.permissions.includes('survey.edit')) return <Navigate to={`/surveys/${id}`} replace />;
  return <FlowLoaded key={survey.id} initial={survey} />;
}

function FlowLoaded({ initial }: { initial: SurveyDetail }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [server, setServer] = useState(initial);
  const [definition, setDefinition] = useState<Definition>(initial.definition);
  const revision = useRef(initial.revision);

  const autosave = useAutosave({
    value: definition,
    delay: 700,
    isConflict: (e) => e instanceof ApiError && e.code === 'revision_conflict',
    save: async (def) => {
      const res = await api.saveSurvey(server.id, { title: server.title, description: server.description, requireAuth: server.requireAuth, definition: def, revision: revision.current });
      revision.current = res.revision;
      setServer(res);
    },
  });

  return (
    <>
      <Topbar
        title={t('builder.flow_title')}
        crumbs={[
          { to: '/surveys', label: t('nav.questionnaires') },
          { to: `/surveys/${server.id}`, label: server.title || t('common.untitled_question') },
        ]}
      >
        <SaveIndicator status={autosave.status} savedAt={autosave.savedAt} onRetry={() => void autosave.flush()} />
        <Link className="btn sm" to={`/surveys/${server.id}`}>
          <ArrowLeft /> {t('flow.back_to_builder')}
        </Link>
      </Topbar>
      {autosave.status === 'conflict' && (
        <div className="banner warn mb-16">
          <AlertTriangle />
          <span className="grow">{t('builder.conflict')}</span>
          <button className="btn sm" onClick={() => window.location.reload()}>
            {t('common.reload')}
          </button>
        </div>
      )}
      <div className="card flow-page-card">
        <FlowEditor def={definition} surveyId={server.id} canEdit onChange={(fn) => setDefinition(fn)} onSelect={(qid) => nav(`/surveys/${server.id}`, { state: { select: qid } })} />
      </div>
    </>
  );
}
